import React, { Suspense, useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  Select,
  Image,
  Input,
  Button,
  Spin,
  Modal,
  Drawer,
  Popover,
  Popconfirm,
  Upload,
  Table,
  Switch,
  InputNumber,
  Dropdown,
  Menu,
  Tag
} from 'antd';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ReactPlayer from 'react-player';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InboxOutlined, CloseOutlined } from '@ant-design/icons';
import { faArrowRight, faArrowLeft, faArrowDown, faArrowUp, faTimesCircle, faClipboard, faShareAlt } from '@fortawesome/pro-duotone-svg-icons';
import { notificationsAtom, tokenAtom, userPrivilagesAtom } from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import api from '../../../../api/api';
import { thousand, mapLearningVideoExpertLevel, getLikesAbbrNumber } from '../../../../functions';
import envConfig from '../../../../envConfig';
import { DraggableBodyRow, DragHandle, expertLevels } from '../../../../utils';

function AdminLearning() {
  const defaultCategory = {
    nameEng: '',
    nameSpa: '',
    descriptionEng: '',
    descriptionSpa: '',
    imageUrl: '',
    learningDepartmentId: ''
  }
  const defaultVideo = {
    isActive: false,
    ordering: 0,
    nameEng: '',
    nameSpa: '',
    descriptionEng: '',
    descriptionSpa: '',
    shortDescriptionEng: '',
    shortDescriptionSpa: '',
    urlEng: '',
    urlSpa: '',
    learningCategoryId: '',
    searchTerms: '',
    isPublic: false,
    requiredVideoId: '',
    requiredRoles: '',
    roles: [],
    expertLevel: 0
  }
  const { Dragger } = Upload;
  const { TextArea } = Input;
  const { t, i18n } = useTranslation();
  const { Option } = Select;
  const [width, setWidth] = useState(window.innerWidth);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [loading, setLoading] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [viewDepartments, setViewDepartments] = useState(false);
  const [viewCategories, setViewCategories] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lastSearchTerm, setLastSearchTerm] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchResult, setSearchResult] = useState(null);
  const [levelFilter, setLevelFilter] = useState(-1);
  const [selectedVideo, setSelectedVideo] = useState({
    show: false,
    data: null
  });
  const [selectedDepartment, setSelectedDepartment] = useState({
    show: false,
    filterId: '',
    filter: null,
    data: null
  });
  const [selectedCategory, setSelectedCategory] = useState({
    show: false,
    filterId: '',
    filter: null,
    data: null
  });
  const [departmentImageData, setDepartmentImageData] = useState(null);
  const [categoryImageData, setCategoryImageData] = useState(null);
  const [videoEngData, setVideoEngData] = useState(null);
  const [videoSpaData, setVideoSpaData] = useState(null);
  const [videoPreviewEngData, setVideoPreviewEngData] = useState(null);
  const [videoPreviewSpaData, setVideoPreviewSpaData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [searchRequriedVideos, setSearchRequriedVideos] = useState([]);
  const [loadingRequiredVideo, setLoadingRequiredVideo] = useState(false);

  const departmentDraggerProps = {
    name: 'file',
    accept: 'image/*',
    multiple: false,
    showUploadList: false,
    beforeUpload: (file) => {
      setDepartmentImageData(file);
      return false;
    }
  };

  const categoryDraggerProps = {
    name: 'file',
    accept: 'image/*',
    multiple: false,
    showUploadList: false,
    beforeUpload: (file) => {
      setCategoryImageData(file);
      return false;
    }
  };

  const videoEngDraggerProps = {
    name: 'file',
    accept: 'video/*',
    multiple: false,
    showUploadList: false,
    beforeUpload: (file) => {
      setVideoEngData(file);
      return false;
    }
  }

  const videoSpaDraggerProps = {
    name: 'file',
    accept: 'video/*',
    multiple: false,
    showUploadList: false,
    beforeUpload: (file) => {
      setVideoSpaData(file);
      return false;
    }
  }

  const videoPreviewEngDraggerProps = {
    name: 'file',
    accept: 'video/*',
    multiple: false,
    showUploadList: false,
    beforeUpload: (file) => {
      setVideoPreviewEngData(file);
      return false;
    }
  };

  const videoPreviewSpaDraggerProps = {
    name: 'file',
    accept: 'video/*',
    multiple: false,
    showUploadList: false,
    beforeUpload: (file) => {
      setVideoPreviewSpaData(file);
      return false;
    }
  };

  async function uploadImage(container, tempImageData) {
    const file = tempImageData;
    const formdata = new FormData();
    formdata.append('file', file);
    setUploadingFile(true);
    const response = await api.post(`learning/${container}/uploadfile`, formdata);
    setUploadingFile(false);
    if (response.data.success) {
      return response.data.data;
    }
    setNotifications([
      ...notifications,
      {
        title: t('error'),
        description: response?.data?.error,
        error: true,
      },
    ]);
    return null;
  }

  function getDepartments() {
    if (!loadingDepartments) {
      setLoadingDepartments(true);
      api.get('learning/admin/departments')
      .then((response) => {
        if (response.data.success) {
          setDepartments(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoadingDepartments(false);
      });
    }
  }

  function getCategories() {
    if (!loadingCategories) {
      setLoadingCategories(true);
      api.get('learning/admin/categories')
      .then((response) => {
        if (response.data.success) {
          setCategories(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoadingCategories(false);
      });
    }
  }

  function search(pageToUse, query) {
    if (!loading) {
      setLoading(true);
      setLastSearchTerm(query)
      let url = `learning/admin/videos?page=${pageToUse}&size=${pageSize}&department=${selectedDepartment?.filterId}&category=${selectedCategory?.filterId}`;
      if (levelFilter >= 0) {
          url += `&level=${levelFilter}`;
      }
      if (query?.length > 0) {
          url += `&searchTerm=${query}`;
      }
      api.get(url)
        .then((response) => {
          if (response.data.success) {
            setSearchResult(response.data.data);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response.data.error,
                error: true,
              },
            ]);
          }
        })
        .catch((error) => {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function searchForRequiredVideo(query) {
    if (!loading) {
      setLoadingRequiredVideo(true);
      api.get(`learning/admin/videos?page=0&size=20&searchTerm=${query}`)
        .then((response) => {
          if (response.data.success) {
            setSearchRequriedVideos(response.data.data.videos);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response.data.error,
                error: true,
              },
            ]);
          }
        })
        .catch((error) => {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        })
        .finally(() => {
          setLoadingRequiredVideo(false);
        });
    }
  }

  function reload() {
    search(currentPage, lastSearchTerm)
  }

  function createDepartment() {
    setIsSubmitting(true);
    api.post('learning/admin/department', selectedDepartment.data)
    .then((response) => {
      if (response.data.success) {
        if (response.data.message?.length > 0) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
        }
        setDepartments(response.data.data);
        setSelectedDepartment({
          ...selectedDepartment,
          data: null,
          show: false
        });
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        },
      ]);
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  }

  function updateDepartment() {
    setIsSubmitting(true);
      api.put('learning/admin/department', selectedDepartment.data)
      .then((response) => {
        if (response.data.success) {
          if (response.data.message?.length > 0) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              },
            ]);
          }
          setDepartments(response.data.data);
          getCategories();
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  function createCategory() {
    setIsSubmitting(true);
    api.post('learning/admin/category', selectedCategory.data)
    .then((response) => {
      if (response.data.success) {
        if (response.data.message?.length > 0) {
          setNotifications([
            ...notifications,
            {
              title: t('yay'),
              description: response.data.message,
              error: false,
            },
          ]);
        }
        setCategories(response.data.data);
        setSelectedCategory({
          ...selectedCategory,
          data: null,
          show: false
        });
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        },
      ]);
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  }

  function updateCategory() {
    setIsSubmitting(true);
      api.put('learning/admin/category', selectedCategory.data)
      .then((response) => {
        if (response.data.success) {
          if (response.data.message?.length > 0) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              },
            ]);
          }
          setCategories(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  function createVideo() {
    setIsSubmitting(true);
    api.post('learning/admin/video', {
      ...selectedVideo.data,
      requiredRoles: selectedVideo.data.roles ? selectedVideo.data.roles.join() : ''
    })
      .then((response) => {
        if (response.data.success) {
          if (response.data.message?.length > 0) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              },
            ]);
          }
          setSelectedVideo({
            ...selectedVideo,
            data: null,
            show: false
          });
          search(currentPage, lastSearchTerm);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  function updateVideo() {
    setIsSubmitting(true);
    api.put('learning/admin/video', {
      ...selectedVideo.data,
      requiredRoles: selectedVideo.data.roles ? selectedVideo.data.roles.join() : ''
    })
      .then((response) => {
        if (response.data.success) {
          if (response.data.message?.length > 0) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              },
            ]);
          }
          search(currentPage, lastSearchTerm);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  function updateOrdering() {
    setLoading(true);
    const tempVideos = JSON.parse(JSON.stringify(searchResult.videos));
    for (let i = 1; i <= tempVideos.length; i++) {
      tempVideos[i - 1].ordering = i;
    }
    api.put('learning/admin/videos', tempVideos)
      .then((response) => {
        if (response.data.success) {
          if (response.data.message?.length > 0) {
            setNotifications([
              ...notifications,
              {
                title: t('yay'),
                description: response.data.message,
                error: false,
              },
            ]);
          }
          search(currentPage, lastSearchTerm);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    setSelectedCategory({
      ...selectedCategory,
      filter: null,
      filterId: ''
    });
  }, [selectedDepartment.filterId]);

  useEffect(async () => {
    if (departmentImageData) {
      const result = await uploadImage('departments', departmentImageData);
      if (result?.url) {
        setDepartmentImageData(null);
        setSelectedDepartment({
          ...selectedDepartment,
          data: {
            ...selectedDepartment?.data,
            imageUrl: result?.url
          }
        });
      }
    }
  }, [departmentImageData]);

  useEffect(async () => {
    if (categoryImageData) {
      const result = await uploadImage('categories', categoryImageData);
      if (result?.url) {
        setCategoryImageData(null);
        setSelectedCategory({
          ...selectedCategory,
          data: {
            ...selectedCategory?.data,
            imageUrl: result?.url
          }
        });
      }
    }
  }, [categoryImageData]);

  useEffect(async () => {
    if (videoEngData) {
      const result = await uploadImage('videos', videoEngData);
      if (result?.url) {
        setVideoEngData(null);
        setSelectedVideo({
          ...selectedVideo,
          data: {
            ...selectedVideo?.data,
            urlEng: result?.url
          }
        });
      }
    }
  }, [videoEngData]);

  useEffect(async () => {
    if (videoSpaData) {
      const result = await uploadImage('videos', videoSpaData);
      if (result?.url) {
        setVideoSpaData(null);
        setSelectedVideo({
          ...selectedVideo,
          data: {
            ...selectedVideo?.data,
            urlSpa: result?.url
          }
        });
      }
    }
  }, [videoSpaData]);

  useEffect(async () => {
    if (videoPreviewEngData) {
      const result = await uploadImage('videopreviews', videoPreviewEngData);
      if (result?.url) {
        setVideoPreviewEngData(null);
        setSelectedVideo({
          ...selectedVideo,
          data: {
            ...selectedVideo?.data,
            previewImageUrlEng: result?.url
          }
        });
      }
    }
  }, [videoPreviewEngData]);

  useEffect(async () => {
    if (videoPreviewSpaData) {
      const result = await uploadImage('videopreviews', videoPreviewSpaData);
      if (result?.url) {
        setVideoPreviewSpaData(null);
        setSelectedVideo({
          ...selectedVideo,
          data: {
            ...selectedVideo?.data,
            previewImageUrlSpa: result?.url
          }
        });
      }
    }
  }, [videoPreviewSpaData]);

  useEffect(() => {
    search(0, lastSearchTerm);
  }, [pageSize]);
  // }, [selectedDepartment.filter, selectedCategory.filter, pageSize, levelFilter]);

  useEffect(() => {
    setWidth(window.innerWidth);
  }, [window.innerWidth]);

  useEffect(() => {
    setSelectedVideo({
      ...selectedVideo,
      query: '',
      previewUrl: ''
    })
  }, [selectedVideo.show]);

  useEffect(() => {
    getDepartments();
    getCategories();
    setCurrentPage(0);
    search(0, '');
  }, []);

  const mobileColumns = [
    {
      title: '',
      dataIndex: 'ordering',
      width: 30,
      key: 'ordering',
      render: () => <DragHandle />,
    },
    {
      title: t('video'),
      align: 'center',
      className: 'text-sm',
      render: (row) => (
        <div className="w-full sapce-y-2">
          <p className="font-bold">{i18n.language === 'en' ? row.nameEng : row.nameSpa}</p>
          <p>{i18n.language === 'en' ? row?.category?.nameEng : row?.category?.nameSpa} @ {i18n.language === 'en' ? row?.category?.department?.nameEng : row?.category?.department?.nameSpa}</p>
          <Tag>{t(mapLearningVideoExpertLevel(row.expertLevel))}</Tag>
          <div className="grid grid-cols-2">
            <div className="text-left">
              <p>{t('ordering')}</p>
              <p>{row.ordering}</p>
            </div>
            <div className="text-right">
              <p>{t('likes')}</p>
              <p>{getLikesAbbrNumber(row.likes)}</p>
            </div>
          </div>
          <div className="flex text-center space-x-2">
            <Button
              size="small"
              type="primary"
              className="mx-auto"
              onClick={() => {
                setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...row,
                    roles: row.requiredRoles?.split(',')
                  },
                  show: true
                });
              }}
            >
              {t('edit')}
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/learning/video/${row.id}`)
                setNotifications([
                  ...notifications,
                  {
                    title: t('yay'),
                    description: t('video_url_copied'),
                    error: false,
                  },
                ]);
              }}
            >
              {<FontAwesomeIcon icon={faShareAlt} />}{t('URL')}
            </Button>
          </div>
        </div>
      ),
    }
  ];

  const columns = [
    {
      title: '',
      dataIndex: 'ordering',
      width: 30,
      key: 'ordering',
      render: () => <DragHandle />,
    },
    { 
      width: 100,
      title: t('ordering'),
      align: 'center',
      className: 'hidden md:table-cell text-sm',
      render: (row) => (
        <span>{row.ordering}</span>
      ),
    },
    {
      title: t('name'),
      align: 'left',
      className: 'hidden md:table-cell text-sm',
      render: (row) => (
        <span>{i18n.language === 'en' ? row.nameEng : row.nameSpa}</span>
      ),
    },
    {
      title: t('category'),
      align: 'center',
      className: 'hidden md:table-cell text-sm',
      render: (row) => (
        <span>{i18n.language === 'en' ? row?.category?.nameEng : row?.category?.nameSpa} @ {i18n.language === 'en' ? row?.category?.department?.nameEng : row?.category?.department?.nameSpa}</span>
      ),
    },
    {
      title: t('likes'),
      align: 'center',
      className: 'hidden md:table-cell text-sm',
      render: (row) => (
        <span>{getLikesAbbrNumber(row.likes)}</span>
      ),
    },
    {
      title: t('expert_level'),
      align: 'center',
      className: 'hidden md:table-cell text-sm',
      render: (row) => (
        <Tag>{t(mapLearningVideoExpertLevel(row.expertLevel))}</Tag>
      ),
    },
    {
      width: 100,
      align: 'center',
      className: 'hidden md:table-cell text-sm',
      render: (row) => (
        <div className="flex space-x-2">
          <Button
            size="small"
            type="primary"
            onClick={() => {
              setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...row,
                  roles: row.requiredRoles?.split(',')
                },
                show: true
              });
            }}
          >
            {t('edit')}
          </Button>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/learning/video/${row.id}`)
              setNotifications([
                ...notifications,
                {
                  title: t('yay'),
                  description: t('video_url_copied'),
                  error: false,
                },
              ]);
            }}
          >
            {<FontAwesomeIcon icon={faShareAlt} />}{t('URL')}
          </Button>
        </div>
      ),
    }
  ];

  return (
    <MainPage includeBack requiresLearningManager>
      <div className="flex mt-4">
        <H1 text={t('learning_center')} />
        <div className="flex ml-auto space-x-2">
          <Popconfirm
            title={t('update_ordering_in_place_prompt')}
            okText={t('yes')}
            cancelText={t('no')}
            onConfirm={() => updateOrdering()}
          >
            <Button
              key="updateordering"
              size="small"
              type="primary"
              disabled={loading || searchResult?.videos?.length === 0}
            >
              {t('update_ordering')}
            </Button>
          </Popconfirm>
          <Button
            key="viewdepts"
            size="small"
            type="primary"
            disabled={loadingDepartments}
            onClick={() => setViewDepartments(true)}
          >
            {t('departments')}
          </Button>
          <Button
            key="viewcats"
            size="small"
            type="primary"
            disabled={loadingCategories || departments?.length === 0}
            onClick={() => setViewCategories(true)}
          >
            {t('categories')}
          </Button>
          <Button
            key="createvideo"
            size="small"
            type="primary"
            disabled={loading}
            onClick={() => setSelectedVideo({
              ...selectedDepartment,
              data: defaultVideo,
              show: true
            })}
          >
            {t('create')}
          </Button>
          <Button
            key="reload"
            size="small"
            disabled={loading}
            type="primary"
            onClick={() => reload()}
          >
            {t('reload')}
          </Button>
        </div>
      </div>
      <div className="flex space-x-2">
        <Input.Search
          placeholder={`${t('search_videos')}...`}
          allowClear
          loading={loading}
          enterButton={t('search')}
          onClear={() => {
            setCurrentPage(0);
            search(0, '');
          }}
          onSearch={(val) => {
            setCurrentPage(0);
            search(0, val);
          }}
        />
        <Select
          showSearch
          filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
          className="w-52 z-10"
          disabled={loading}
          value={selectedDepartment?.filterId}
          onChange={(value) => {
            if (value !== '') {
              setSelectedDepartment({
                ...selectedDepartment,
                filter: _.filter(departments, (d) => d.id === value)[0],
                filterId: value
              });
            } else {
              setSelectedDepartment({
                ...selectedDepartment,
                filter: null,
                filterId: ''
              });
            }
          }}
        >
          <Option
            key="any"
            value=""
            className="text-sm flex items-center"
          >
            {t('any_department')}
          </Option>
          {_.map(departments, (department) => (
            <Option
              key={department.id}
              value={department.id}
              className="text-sm flex items-center"
            >
              {i18n.language === 'en' ? department.nameEng : department.nameSpa}
            </Option>
          ))}
        </Select>
        <Select
          showSearch
          filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
          className="w-52 z-10"
          disabled={loading || !selectedDepartment?.filter}
          value={selectedCategory?.filterId}
          onChange={(value) => {
            if (value !== '') {
              setSelectedCategory({
                ...selectedCategory,
                filter: _.filter(categories, (c) => c.id === value)[0],
                filterId: value
              });
            } else {
              setSelectedCategory({
                ...selectedCategory,
                filter: null,
                filterId: ''
              });
            }
          }}
        >
          <Option
            key="any"
            value=""
            className="text-sm flex items-center"
          >
            {t('any_category')}
          </Option>
          {_.map(selectedDepartment?.filter?.categories, (category) => (
            <Option
              key={category.id}
              value={category.id}
              className="text-sm flex items-center"
            >
              {i18n.language === 'en' ? category.nameEng : category.nameSpa}
            </Option>
          ))}
        </Select>
        <Select
          className="w-44 z-10"
          disabled={loading}
          value={levelFilter}
          onChange={(value) => setLevelFilter(value)}
        >
          <Option
            key={-1}
            value={-1}
            className="text-sm flex items-center"
          >
            {t('any_level')}
          </Option>
          {_.map(expertLevels, (level) => (
            <Option
              key={level.id}
              value={level.id}
              className="text-sm flex items-center"
            >
              {t(level.name)}
            </Option>
          ))}
        </Select>
      </div>
      <DndContext
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={(active, over) => {
          const temp = JSON.parse(JSON.stringify(searchResult));
          if (active?.active?.id !== active?.over?.id) {
            setSearchResult((prevState) => {
              const activeIndex = prevState.videos.findIndex((record) => record.id === active?.active?.id);
              const overIndex = prevState.videos.findIndex((record) => record.id === active?.over?.id);
              const newData = arrayMove(prevState.videos, activeIndex, overIndex);
              temp.videos = JSON.parse(JSON.stringify(newData));
              return temp;
            });
          }
        }}
      >
        <SortableContext items={_.map(searchResult?.videos, (i) => i.id)} strategy={verticalListSortingStrategy}>
          <Table
            bordered
            className="z-0 w-full"
            dataSource={searchResult?.videos}
            locale={{
              emptyText: t('no_data')
            }}
            loading={loading}
            pagination={{
              pageSize,
              showSizeChanger: true,
              current: currentPage + 1,
              total: searchResult?.total,
              onChange: (page, size) => {
                if (page - 1 !== currentPage) {
                  setCurrentPage(page - 1);
                  search(page - 1, lastSearchTerm);
                } else {
                  setPageSize(size);
                }
              }
            }}
            rowKey="id"
            components={{
              body: {
                row: DraggableBodyRow,
              },
            }}
            columns={width < 1024 ? mobileColumns : columns}
          />
        </SortableContext>
      </DndContext>

      <Drawer
        title={t('departments')}
        placement="right"
        closable={!loading}
        onClose={() => setViewDepartments(false)}
        open={viewDepartments}
        width={width < 500 ? width : 500}
      >
        <div className="flex flex-col">
          <div className="flex ml-auto space-x-2">
            <Button
              key="createdept"
              size="small"
              type="primary"
              disabled={loadingDepartments}
              onClick={() => setSelectedDepartment({
                ...selectedDepartment,
                data: defaultCategory,
                show: true
              })}
            >
              {t('create')}
            </Button>
            <Button
              key="reload"
              size="small"
              type="primary"
              loading={loadingDepartments}
              onClick={() => getDepartments()}
            >
              {t('reload')}
            </Button>
          </div>
          <div className="divide-y mt-2 space-y-2">
            {_.map(departments, (department) => (
              <button
                className="relative h-24 cursor-pointer border-2 border-gray-300 rounded-md py-4 px-2 w-full overflow-hidden"
                type="button"
                onClick={() => setSelectedDepartment({
                  ...selectedDepartment,
                  data: department,
                  show: true
                })}
              >
                {department?.imageUrl && (
                  <div
                    className="bg-cover bg-center absolute top-0 left-0 right-0 bottom-0 opacity-40"
                    style={{ backgroundImage: `url(${department?.imageUrl})` }}
                  />
                )}
                <div className="absolute top-0 left-0 right-0 bottom-0">
                  <div className="flex flex-col h-full p-2">
                    <div className="my-auto">
                      <p className="text-lg font-bold">
                        {i18n.language === 'en' ? department.nameEng : department.nameSpa}
                      </p>
                      <p className="text-left truncate">
                        {i18n.language === 'en' ? department.descriptionEng : department.descriptionSpa}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Drawer>

      <Drawer
        title={t('categories')}
        placement="right"
        closable={!loading}
        onClose={() => setViewCategories(false)}
        open={viewCategories}
        width={width < 500 ? width : 500}
      >
        <div className="flex flex-col">
          <div className="flex ml-auto space-x-2">
            <Button
              key="createcategory"
              size="small"
              type="primary"
              disabled={loadingCategories}
              onClick={() => setSelectedCategory({
                ...selectedCategory,
                data: defaultCategory,
                show: true
              })}
            >
              {t('create')}
            </Button>
            <Button
              key="reload"
              size="small"
              type="primary"
              loading={loadingCategories}
              onClick={() => getCategories()}
            >
              {t('reload')}
            </Button>
          </div>
          <div className="divide-y mt-2 space-y-2">
            {_.map(categories, (category) => (
              <button
                className="relative h-24 cursor-pointer rounded-md border-2 border-gray-300 py-4 px-2 w-full overflow-hidden"
                type="button"
                onClick={() => setSelectedCategory({
                  ...selectedCategory,
                  data: category,
                  show: true
                })}
              >
                {category?.imageUrl && (
                  <div
                    className="bg-cover bg-center absolute top-0 left-0 right-0 bottom-0 opacity-40"
                    style={{ backgroundImage: `url(${category?.imageUrl})` }}
                  />
                )}
                <div className="absolute top-0 left-0 right-0 bottom-0">
                  <div className="flex flex-col h-full p-2">
                    <div className="my-auto">
                      <p className="text-lg font-bold">
                        {i18n.language === 'en' ? category.nameEng : category.nameSpa}
                      </p>
                      <p className="text-left truncate">
                        {i18n.language === 'en' ? category.descriptionEng : category.descriptionSpa}
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Drawer>

      <Modal
        title={selectedDepartment.data?.id ? t('edit_department') : t('create_department')}
        footer={[
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={uploadingFile || selectedDepartment?.data?.nameEng?.length === 0 || selectedDepartment?.data?.nameSpa?.length === 0}
            onClick={() => (selectedDepartment.data?.id ? updateDepartment() : createDepartment())}
          >
            {t('submit')}
          </Button>
        ]}
        closable={!isSubmitting || !uploadingFile}
        open={selectedDepartment.show}
        onCancel={() => setSelectedDepartment({
            ...selectedDepartment,
            show: false,
            data: null
        })}
      >
        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('name')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedDepartment.data?.nameEng?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('name')}
              onChange={(obj) => setSelectedDepartment({
                ...selectedDepartment,
                data: {
                  ...selectedDepartment.data,
                  nameEng: obj.target.value
                }
              })}
              value={selectedDepartment.data?.nameEng}
            />
          </div>
          <div className="mt-4">
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedDepartment({
                  ...selectedDepartment,
                  data: {
                    ...selectedDepartment.data,
                    nameSpa: selectedDepartment.data.nameEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedDepartment({
                  ...selectedDepartment,
                  data: {
                    ...selectedDepartment.data,
                    nameEng: selectedDepartment.data.nameSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('name_spa')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedDepartment.data?.nameSpa?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('name_spa')}
              onChange={(obj) => setSelectedDepartment({
                ...selectedDepartment,
                data: {
                  ...selectedDepartment.data,
                  nameSpa: obj.target.value
                }
              })}
              value={selectedDepartment.data?.nameSpa}
            />
          </div>
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('description')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedDepartment.data?.descriptionEng?.length}/500)
              </span>
            </p>
            <TextArea
              rows={3}
              disabled={isSubmitting}
              placeholder={t('description')}
              onChange={(obj) => setSelectedDepartment({
                ...selectedDepartment,
                data: {
                  ...selectedDepartment.data,
                  descriptionEng: obj.target.value
                }
              })}
              value={selectedDepartment.data?.descriptionEng}
            />
          </div>
          <div className="mt-8">
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedDepartment({
                  ...selectedDepartment,
                  data: {
                    ...selectedDepartment.data,
                    descriptionSpa: selectedDepartment.data.descriptionEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedDepartment({
                  ...selectedDepartment,
                  data: {
                    ...selectedDepartment.data,
                    descriptionEng: selectedDepartment.data.descriptionSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('description_spa')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedDepartment.data?.descriptionSpa?.length}/500)
              </span>
            </p>
            <TextArea
              rows={3}
              disabled={isSubmitting}
              placeholder={t('description_spa')}
              onChange={(obj) => setSelectedDepartment({
                ...selectedDepartment,
                data: {
                  ...selectedDepartment.data,
                  descriptionSpa: obj.target.value
                }
              })}
              value={selectedDepartment.data?.descriptionSpa}
            />
          </div>
        </div>

        <div>
          <p className="space-x-1">
            <strong>{t('image')}</strong>
          </p>
          <Spin spinning={uploadingFile}>
            <Dragger disabled={uploadingFile || isSubmitting} {...departmentDraggerProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t('click_or_drag_content')}
              </p>
            </Dragger>
          </Spin>
          {selectedDepartment.data?.imageUrl && (
            <div className="flex space-x-2">
              <Image
                alt="department"
                width={60}
                src={selectedDepartment.data?.imageUrl}
                className="mt-2 cursor-pointer"
              />
              <Button
                disabled={uploadingFile || isSubmitting}
                type="primary"
                danger
                className="mt-6"
                onClick={() => {
                  setSelectedDepartment({
                    ...selectedDepartment,
                    data: {
                      ...selectedDepartment?.data,
                      imageUrl: ''
                    }
                  });
                }}
              >
                <CloseOutlined />
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={selectedCategory.data?.id ? t('edit_category') : t('create_category')}
        footer={[
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={uploadingFile || selectedCategory?.data?.nameEng?.length === 0 || selectedCategory?.data?.nameSpa?.length === 0 || selectedCategory?.data?.learningDepartmentId?.length === 0}
            onClick={() => (selectedCategory.data?.id ? updateCategory() : createCategory())}
          >
            {t('submit')}
          </Button>
        ]}
        closable={!isSubmitting || !uploadingFile}
        open={selectedCategory.show}
        onCancel={() => setSelectedCategory({
            ...selectedCategory,
            show: false,
            data: null
        })}
      >
        <div className="mb-4">
          <p className="space-x-1">
            <strong>{t('department')}</strong>
          </p>
          <Select
            showSearch
            filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
            className="w-full"
            value={selectedCategory?.data?.learningDepartmentId}
            onChange={(value) => setSelectedCategory({
              ...selectedCategory,
              data: {
                ...selectedCategory.data,
                learningDepartmentId: value
              }
            })}
          >
            {_.map(departments, (department) => (
              <Option
                key={department.id}
                value={department.id}
                className="text-sm flex items-center"
              >
                {i18n.language === 'en' ? department.nameEng : department.nameSpa}
              </Option>
            ))}
          </Select>
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('name')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedCategory.data?.nameEng?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('name')}
              onChange={(obj) => setSelectedCategory({
                ...selectedCategory,
                data: {
                  ...selectedCategory.data,
                  nameEng: obj.target.value
                }
              })}
              value={selectedCategory.data?.nameEng}
            />
          </div>
          <div className="mt-4">
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedCategory({
                  ...selectedCategory,
                  data: {
                    ...selectedCategory.data,
                    nameSpa: selectedCategory.data.nameEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedCategory({
                  ...selectedCategory,
                  data: {
                    ...selectedCategory.data,
                    nameEng: selectedCategory.data.nameSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('name_spa')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedCategory.data?.nameSpa?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('name_spa')}
              onChange={(obj) => setSelectedCategory({
                ...selectedCategory,
                data: {
                  ...selectedCategory.data,
                  nameSpa: obj.target.value
                }
              })}
              value={selectedCategory.data?.nameSpa}
            />
          </div>
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('description')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedCategory.data?.descriptionEng?.length}/500)
              </span>
            </p>
            <TextArea
              rows={3}
              disabled={isSubmitting}
              placeholder={t('description')}
              onChange={(obj) => setSelectedCategory({
                ...selectedCategory,
                data: {
                  ...selectedCategory.data,
                  descriptionEng: obj.target.value
                }
              })}
              value={selectedCategory.data?.descriptionEng}
            />
          </div>
          <div className="mt-8">
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedCategory({
                  ...selectedCategory,
                  data: {
                    ...selectedCategory.data,
                    descriptionSpa: selectedCategory.data.descriptionEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedCategory({
                  ...selectedCategory,
                  data: {
                    ...selectedCategory.data,
                    descriptionEng: selectedCategory.data.descriptionSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('description_spa')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedCategory.data?.descriptionSpa?.length}/500)
              </span>
            </p>
            <TextArea
              rows={3}
              disabled={isSubmitting}
              placeholder={t('description_spa')}
              onChange={(obj) => setSelectedCategory({
                ...selectedCategory,
                data: {
                  ...selectedCategory.data,
                  descriptionSpa: obj.target.value
                }
              })}
              value={selectedCategory.data?.descriptionSpa}
            />
          </div>
        </div>

        <div>
          <p className="space-x-1">
            <strong>{t('image')}</strong>
          </p>
          <Spin spinning={uploadingFile}>
            <Dragger disabled={uploadingFile || isSubmitting} {...categoryDraggerProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t('click_or_drag_content')}
              </p>
            </Dragger>
          </Spin>
          {selectedCategory.data?.imageUrl && (
            <div className="flex space-x-2">
              <Image
                alt="department"
                width={60}
                src={selectedCategory.data?.imageUrl}
                className="mt-2 cursor-pointer"
              />
              <Button
                disabled={uploadingFile || isSubmitting}
                type="primary"
                danger
                className="mt-6"
                onClick={() => {
                  setSelectedCategory({
                    ...selectedCategory,
                    data: {
                      ...selectedCategory?.data,
                      imageUrl: ''
                    }
                  });
                }}
              >
                <CloseOutlined />
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={selectedVideo.data?.id ? t('edit_video') : t('create_video')}
        footer={[
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={uploadingFile || selectedVideo?.data?.nameEng?.length === 0 || selectedVideo?.data?.nameSpa?.length === 0 ||
                selectedVideo?.data?.descriptionEng?.length === 0 || selectedVideo?.data?.descriptionSpa?.length === 0 ||
                selectedVideo?.data?.shortDescriptionEng?.length === 0 || selectedVideo?.data?.shortDescriptionSpa?.length === 0 ||
                selectedVideo?.data?.urlEng?.length === 0 || selectedVideo?.data?.urlSpa?.length === 0 ||
                selectedVideo?.data?.previewImageUrlEng?.length === 0 || selectedVideo?.data?.previewImageUrlSpa?.length === 0 ||
                selectedVideo?.data?.learningCategoryId?.length === 0}
            onClick={() => (selectedVideo.data?.id ? updateVideo() : createVideo())}
          >
            {t('submit')}
          </Button>
        ]}
        closable={!isSubmitting || !uploadingFile}
        open={selectedVideo?.show && !selectedVideo?.previewUrl}
        onCancel={() => setSelectedVideo({
            ...selectedVideo,
            show: false,
            data: null
        })}
      >
        <div className="flex space-x-2 mb-4">
          <div className="w-full space-x-1">
            <span className="space-x-1">
              <strong>{t('is_active')}</strong>
            </span>
            <Switch
              disabled={isSubmitting}
              checked={selectedVideo.data?.isActive}
              onChange={(checked) => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo?.data,
                    isActive: checked
                  }
              })}
            />
          </div>
          <div className="w-full space-x-1">
            <span className="space-x-1">
              <strong>{t('is_public')}</strong>
            </span>
            <Switch
              disabled={isSubmitting}
              checked={selectedVideo.data?.isPublic}
              onChange={(checked) => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo?.data,
                    isPublic: checked
                  }
              })}
            />
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('url')}</strong>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('url')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  urlEng: obj.target.value
                }
              })}
              value={selectedVideo.data?.urlEng}
              addonAfter={(
                <Button
                  size="small"
                  type="primary"
                  disabled={selectedVideo.data?.urlEng?.length === 0 || isSubmitting}
                  onClick={() => setSelectedVideo({
                    ...selectedVideo,
                    previewUrl: selectedVideo.data?.urlEng
                  })}
                >
                  {t('preview')}
                </Button>
              )}
            />
            <Spin spinning={uploadingFile}>
              <Dragger disabled={uploadingFile || isSubmitting} {...videoEngDraggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('click_or_drag_content')}
                </p>
              </Dragger>
            </Spin>
          </div>
          <div className="flex">
            <Popover content={t('copy_over')}>
              <Button
                className="w-6 ml-auto mr-1"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    urlSpa: selectedVideo.data.urlEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="w-6 mr-auto ml-1"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    urlEng: selectedVideo.data.urlSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('url_spa')}</strong>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('url_spa')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  urlSpa: obj.target.value
                }
              })}
              value={selectedVideo.data?.urlSpa}
              addonAfter={(
                <Button
                  size="small"
                  type="primary"
                  disabled={selectedVideo.data?.urlSpa?.length === 0 || isSubmitting}
                  onClick={() => setSelectedVideo({
                    ...selectedVideo,
                    previewUrl: selectedVideo.data?.urlSpa
                  })}
                >
                  {t('preview')}
                </Button>
              )}
            />
            <Spin spinning={uploadingFile}>
              <Dragger disabled={uploadingFile || isSubmitting} {...videoSpaDraggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('click_or_drag_content')}
                </p>
              </Dragger>
            </Spin>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('preview_image_url')}</strong>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('preview_image_url')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  previewImageUrlEng: obj.target.value
                }
              })}
              value={selectedVideo.data?.previewImageUrlEng}
            />
            <Spin spinning={uploadingFile}>
              <Dragger disabled={uploadingFile || isSubmitting} {...videoPreviewEngDraggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('click_or_drag_content')}
                </p>
              </Dragger>
            </Spin>
            {selectedVideo.data?.previewImageUrlEng && (
              <div className="flex space-x-2">
                <Image
                  alt="previewimageeng"
                  width={60}
                  src={selectedVideo.data?.previewImageUrlEng}
                  className="mt-2 cursor-pointer"
                />
                <Button
                  disabled={uploadingFile || isSubmitting}
                  type="primary"
                  danger
                  className="mt-6"
                  onClick={() => {
                    setSelectedVideo({
                      ...selectedVideo,
                      data: {
                        ...selectedVideo?.data,
                        previewImageUrlEng: ''
                      }
                    });
                  }}
                >
                  <CloseOutlined />
                </Button>
              </div>
            )}
          </div>
          <div className="flex">
            <Popover content={t('copy_over')}>
              <Button
                className="w-6 ml-auto mr-1"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    previewImageUrlSpa: selectedVideo.data.previewImageUrlEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="w-6 mr-auto ml-1"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    previewImageUrlEng: selectedVideo.data.previewImageUrlSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('preview_image_url_spa')}</strong>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('preview_image_url_spa')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  previewImageUrlSpa: obj.target.value
                }
              })}
              value={selectedVideo.data?.previewImageUrlSpa}
            />
            <Spin spinning={uploadingFile}>
              <Dragger disabled={uploadingFile || isSubmitting} {...videoPreviewSpaDraggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('click_or_drag_content')}
                </p>
              </Dragger>
            </Spin>
            {selectedVideo.data?.previewImageUrlSpa && (
              <div className="flex space-x-2">
                <Image
                  alt="previewimagespa"
                  width={60}
                  src={selectedVideo.data?.previewImageUrlSpa}
                  className="mt-2 cursor-pointer"
                />
                <Button
                  disabled={uploadingFile || isSubmitting}
                  type="primary"
                  danger
                  className="mt-6"
                  onClick={() => {
                    setSelectedVideo({
                      ...selectedVideo,
                      data: {
                        ...selectedVideo?.data,
                        previewImageUrlSpa: ''
                      }
                    });
                  }}
                >
                  <CloseOutlined />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('name')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedVideo.data?.nameEng?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('name')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  nameEng: obj.target.value
                }
              })}
              value={selectedVideo.data?.nameEng}
            />
          </div>
          <div className="mt-4">
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    nameSpa: selectedVideo.data.nameEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    nameEng: selectedVideo.data.nameSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('name_spa')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedVideo.data?.nameSpa?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('name_spa')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  nameSpa: obj.target.value
                }
              })}
              value={selectedVideo.data?.nameSpa}
            />
          </div>
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('short_description')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedVideo.data?.shortDescriptionEng?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('short_description')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  shortDescriptionEng: obj.target.value
                }
              })}
              value={selectedVideo.data?.shortDescriptionEng}
            />
          </div>
          <div className="mt-4">
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    shortDescriptionSpa: selectedVideo.data.shortDescriptionEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    shortDescriptionEng: selectedVideo.data.shortDescriptionSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('short_description_spa')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedVideo.data?.shortDescriptionSpa?.length}/250)
              </span>
            </p>
            <Input
              disabled={isSubmitting}
              placeholder={t('short_description_spa')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  shortDescriptionSpa: obj.target.value
                }
              })}
              value={selectedVideo.data?.shortDescriptionSpa}
            />
          </div>
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('description')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedVideo.data?.descriptionEng?.length}/500)
              </span>
            </p>
            <TextArea
              rows={3}
              disabled={isSubmitting}
              placeholder={t('description')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  descriptionEng: obj.target.value
                }
              })}
              value={selectedVideo.data?.descriptionEng}
            />
          </div>
          <div className="mt-8">
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    descriptionSpa: selectedVideo.data.descriptionEng  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </Popover>
            <Popover content={t('copy_over')}>
              <Button
                className="h-6"
                type="primary"
                disabled={isSubmitting}
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    descriptionEng: selectedVideo.data.descriptionSpa  
                  }
                })}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </Popover>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('description_spa')}</strong>
              <span className="font-light text-xs text-gray-400">
                ({selectedVideo.data?.descriptionSpa?.length}/500)
              </span>
            </p>
            <TextArea
              rows={3}
              disabled={isSubmitting}
              placeholder={t('description_spa')}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  descriptionSpa: obj.target.value
                }
              })}
              value={selectedVideo.data?.descriptionSpa}
            />
          </div>
        </div>

        <div className="mb-4">
          <p className="space-x-1">
            <strong>{t('category')}</strong>
          </p>
          <Select
            showSearch
            filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
            className="w-full"
            disabled={isSubmitting}
            value={selectedVideo?.data?.learningCategoryId}
            onChange={(value) => setSelectedVideo({
              ...selectedVideo,
              data: {
                ...selectedVideo.data,
                learningCategoryId: value
              }
            })}
          >
            {_.map(categories, (category) => (
              <Option
                key={category.id}
                value={category.id}
                className="text-sm flex items-center"
              >
                {i18n.language === 'en' ? category.nameEng : category.nameSpa}
              </Option>
            ))}
          </Select>
        </div>

        <div className="w-full mb-4">
          <p className="space-x-1">
            <strong>{t('search_tags')}</strong>
            <span className="font-light text-xs text-gray-400">
              ({selectedVideo.data?.searchTerms?.length}/250)
            </span>
          </p>
          <Input
            disabled={isSubmitting}
            placeholder={t('search_tags')}
            onChange={(obj) => setSelectedVideo({
              ...selectedVideo,
              data: {
                ...selectedVideo.data,
                searchTerms: obj.target.value
              }
            })}
            value={selectedVideo.data?.searchTerms}
          />
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('expert_level')}</strong>
            </p>
            <Select
              className="w-full"
              disabled={isSubmitting}
              value={selectedVideo?.data?.expertLevel}
              onChange={(value) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  expertLevel: value
                }
              })}
            >
              {_.map(expertLevels, (level) => (
                <Option
                  key={level.id}
                  value={level.id}
                  className="text-sm flex items-center"
                >
                  {t(level.name)}
                </Option>
              ))}
            </Select>
          </div>
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('ordering')}</strong>
            </p>
            <InputNumber
              className="w-full"
              disabled={isSubmitting}
              onChange={(obj) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  ordering: obj
                }
              })}
              value={selectedVideo.data?.ordering}
            />
          </div>
        </div>

        <div className="flex space-x-2 mb-4">
          <div className="w-full">
            <p className="space-x-1">
              <strong>{t('required_roles')}</strong>
            </p>
            <Select
              mode="multiple"
              className="w-full"
              disabled={isSubmitting}
              value={selectedVideo?.data?.roles}
              onChange={(value) => setSelectedVideo({
                ...selectedVideo,
                data: {
                  ...selectedVideo.data,
                  roles: value
                }
              })}
            >
              {_.map(envConfig.Roles, (role) => (
                <Option
                  key={role.key}
                  value={role.key}
                  className="text-sm flex items-center"
                >
                  {t(role.name)}
                </Option>
              ))}
            </Select>
          </div>
        </div>

        <div className="w-full mb-4">
          <p className="space-x-1">
            <strong>{t('required_video')}</strong>
          </p>
          <Dropdown
            trigger="click"
            placement="bottom"
            overlay={
              <Menu>
                {_.map(searchRequriedVideos, (v) => (
                  <Menu.Item
                    key={v.id}
                  >
                    <button
                      type="button"
                      onClick={() => {
                      setSelectedVideo({
                        ...selectedVideo,
                        query: '',
                        data: {
                          ...selectedVideo.data,
                          requiredVideo: v,
                          requiredVideoId: v.id
                        }
                      })
                      setSearchRequriedVideos([]);
                     }}
                    >
                      <p>{i18n.language === 'en' ? v?.nameEng : v?.nameSpa}</p>
                    </button>
                  </Menu.Item>
                ))}
              </Menu>
            }
          >
            <Input.Search
              placeholder={`${t('search_videos')}...`}
              allowClear
              disabled={isSubmitting}
              loading={loadingRequiredVideo}
              enterButton={t('search')}
              size="small"
              value={selectedVideo?.query}
              onChange={(obj) => {
                setSelectedVideo({
                  ...selectedVideo,
                  query: obj.target.value
                })
                if (obj.target.value.length === 0) {
                  setSearchRequriedVideos([]);
                }
              }}
              onClear={() => setSearchRequriedVideos([])}
              onSearch={(val) => searchForRequiredVideo(val)}
            />
          </Dropdown>
          {!selectedVideo?.data?.requiredVideoId && (
            <p className="font-light italic text-gray-400">{t('no_required_video')}</p>
          )}
          {selectedVideo?.data?.requiredVideoId && (
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  previewUrl: i18n.language === 'en' ? selectedVideo?.data?.requiredVideo?.urlEng : selectedVideo?.data?.requiredVideo?.urlSpa
                })}
              >
                <p>{i18n.language === 'en' ? selectedVideo?.data?.requiredVideo?.nameEng : selectedVideo?.data?.requiredVideo?.nameSpa}</p>
              </button>
              <button
                type="button"
                onClick={() => setSelectedVideo({
                  ...selectedVideo,
                  data: {
                    ...selectedVideo.data,
                    requiredVideo: null,
                    requiredVideoId: null
                  }
                })}
              >
                <FontAwesomeIcon icon={faTimesCircle} className="text-red-400" />
              </button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={i18n.language === 'en' ? selectedVideo?.data?.nameEng : selectedVideo?.data?.nameSpa}
        open={selectedVideo?.previewUrl?.length > 0}
        onCancel={() => setSelectedVideo({
          ...selectedVideo,
          previewUrl: null
        })}
        footer={[]}
      >
        <Suspense
          fallback={(
            <></>
          )}
        >
          <ReactPlayer className="mt-8" style={{ width: '100%', height: 'auto', aspectRatio: '16/9' }} controls={true} volume={0.25} src={selectedVideo?.previewUrl} />
        </Suspense>
      </Modal>
    </MainPage>
  );
}

export default AdminLearning;
